import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemText,
  Collapse,
  Typography,
  Box,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack,
  CorporateFare,
  Delete,
  FilterList,
  FormatListBulletedSharp,
  GridViewSharp,
  Assignment as AssignmentIcon,
  Engineering,
  UploadFile,
} from "@mui/icons-material";
import { getProperties } from "selectors/fileManagerSelectors";
import { propertyGetAll } from "actions/propertyActions";
import { selectAllFiles, selectIsFetching } from "selectors/documentsSelectors";
import { deleteFile, fetchFiles } from "actions/documentsActions";
// import ImageIcon from "@mui/icons-material/Image";
// import NoteIcon from "@mui/icons-material/Note";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import ArticleIcon from "@mui/icons-material/Article";
import FileManagerList from "components/FileManagerList/FileManager";
import FileGrid from "./FileGrid";
import { palette } from "themes";
import FolderMinus from "../../assets/properties/Folder-minus.png";
import FolderPlus from "../../assets/properties/Folder-plus.png";
import { Button } from "react-bootstrap";
import Filter from "./Filter";
import { fetchJobsByProjectId } from "actions/fileManagerActions";
import HiddenOn from "components/Wrappers/HiddenOn";
import { ModelDialog } from "components/Model";
import FileManagerUploadContent, {
  FileManagerUploadHeader,
} from "./FileManagerUpload";
import { decodeToken } from "core/utils/appUtils";

const FileManager = () => {
  const theme = useTheme();

  // tablet breakpoint in MUI by default is 900px for 'md', but let's assume 768px for tablet here
  // You can customize this according to your needs or use 'sm' breakpoint (600px) for mobile
  const isLessThanTablet = useMediaQuery(theme.breakpoints.down("sm"));
  // 'sm' means <= 600px, for strict 768px, use a custom media query below

  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const prop = useSelector(getProperties);
  const allFiles = useSelector(selectAllFiles);
  const isFetching = useSelector(selectIsFetching);

  const paginatedProps = prop.propertyList;
  const loading = prop?.loading || false;
  const error = prop?.error || null;
  const jobs_loading = prop?.jobs_loading || false;
  const jobsData = prop?.jobs;
  const paginatedHasMore = prop?.hasMore;

  const [jobs, setJobs] = useState(jobsData || []);
  const [isJobsLoading, setIsJobsLoading] = useState(jobs_loading);
  const [filesBelongsTo, setFilesBelongsTo] = useState("Company");
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("company");
  const [selectedProject, setSelectedProject] = useState(
    params.projectId || null
  );
  const [selectedJob, setSelectedJob] = useState(params.jobId || null);

  const [listTitle, setListTitle] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [hasMore, setHasMore] = useState();
  const [pageNumber, setPageNumber] = useState(1);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const listRef = useRef(null);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    type: "",
    message: "",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [makeDelete, setMakeDelete] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const [openUploadDialog, setOpenUploadDialog] = useState(false);

  const toggleView = (mode) => setViewMode(mode);

  const fetchProperties = useCallback(() => {
    dispatch(propertyGetAll({ pageNumber, pageSize: 15 }));
  }, [dispatch, pageNumber]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    if (paginatedProps) {
      const uniqueProperties = new Set(properties.map((p) => p.propertyId));
      const newProperties = paginatedProps.filter(
        (p) => !uniqueProperties.has(p.propertyId)
      );
      setProperties((prevProperties) => [...prevProperties, ...newProperties]);
    }
    setHasMore(paginatedHasMore);
  }, [paginatedProps]);

  const applyCompanyLevelState = () => {
    setSelectedProperty("company");
    setListTitle("Company Documents");
    setFilesBelongsTo("Company");
  };

  useEffect(() => {
    if (properties.length > 0) {
      const firstProperty = params.propertyId
        ? // eslint-disable-next-line eqeqeq
          properties.find((x) => x.propertyId == params.propertyId)
        : params.propertyId || undefined;

      if (firstProperty === "company") {
        applyCompanyLevelState();
      } else if (firstProperty && firstProperty?.propertyId) {
        setFilesBelongsTo("Property");
        setSelectedProperty(firstProperty.propertyId);
        setListTitle(firstProperty.propertyName);
      } else {
        applyCompanyLevelState();
      }
    } else {
      applyCompanyLevelState();
    }
  }, [properties, params]);

  const fetchEntityFiles = useCallback(() => {
    setSelectedFiles([]);

    if (selectedProperty === "company" && filesBelongsTo === "Company") {
      const userDetails = decodeToken();
      dispatch(fetchFiles("Company", userDetails?.CompanyId));
    }
    if (selectedProject && filesBelongsTo === "Project") {
      dispatch(fetchFiles("Project", selectedProject));
    } else if (selectedProperty && filesBelongsTo === "Property") {
      dispatch(fetchFiles("Property", selectedProperty));
    } else if (selectedJob && filesBelongsTo === "Job") {
      dispatch(fetchFiles("Job", selectedJob));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedProject, selectedProperty, selectedJob]);

  useEffect(() => {
    fetchEntityFiles();
  }, [fetchEntityFiles]);

  useEffect(() => {
    setFilteredFiles(allFiles);
    const uniqueDocumentTypes = [];
    allFiles.forEach((file) => {
      if (file.documentTypeId) {
        if (uniqueDocumentTypes[file.documentTypeId]) {
          uniqueDocumentTypes[file.documentTypeId].count += 1;
        } else {
          uniqueDocumentTypes[file.documentTypeId] = {
            text: file.documentType.documentType,
            id: file.documentTypeId,
            count: 1,
          };
        }
      }
    });

    setDocumentTypes(uniqueDocumentTypes);
  }, [allFiles]);

  const handleToggle = (propertyId, name, belongsTo = "Property") => {
    setSelectedProperty(propertyId);
    setSelectedProject(null);
    setSelectedJob(null);
    setListTitle(name);
    setFilesBelongsTo(belongsTo);
    if (propertyId) {
      navigate(`/filemanager/${propertyId}`);
    }
  };

  const handleToggleSelected = useCallback(
    (projId, name) => {
      setSelectedProject(projId);
      setSelectedJob(null);
      setListTitle(name);
      setFilesBelongsTo("Project");
      dispatch(fetchJobsByProjectId({ projectId: projId }));
      // navigate(`/filemanager/${selectedProperty}/${projId}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const handleSelectedJob = (jobId, name) => {
    setSelectedJob(jobId);
    setListTitle(name);
    setFilesBelongsTo("Job");
    // navigate(`/filemanager/${selectedProperty}/${selectedProject}/${jobId}`);
  };

  const promptDelete = () => {
    setShowConfirmModal(true);
  };

  useEffect(() => {
    setShowConfirmModal(false);
    if (makeDelete) {
      handleDelete();
      setMakeDelete(false);
    }
  }, [makeDelete]);

  useEffect(() => {
    setJobs(jobsData);
    setIsJobsLoading(jobs_loading);
  }, [jobsData, jobs_loading]);

  const handleDelete = async () => {
    try {
      const promiseAll = selectedFiles.map((fileId) =>
        dispatch(deleteFile(fileId))
      );
      await Promise.all(promiseAll);
      setSelectedFiles([]);
      setTimeout(async () => {
        if (selectedProperty === "company" && filesBelongsTo === "Company") {
          const userDetails = decodeToken();
          dispatch(fetchFiles("Company", userDetails?.CompanyId));
        } else if (selectedProperty && filesBelongsTo === "Property") {
          console.log("Deletion success, making fetch call for property");
          await dispatch(fetchFiles("Property", selectedProperty));
        } else if (selectedProject && filesBelongsTo === "Project") {
          await dispatch(fetchFiles("Project", selectedProject));
        } else if (selectedJob && filesBelongsTo === "Job") {
          await dispatch(fetchFiles("Job", selectedJob));
        }
        showSnackbar("success", "Files deleted successfully");
      }, 2000); // 2 seconds timeout
    } catch (error) {
      showSnackbar("error", "Error deleting files");
    }
  };

  const handleSelectAll = () => {
    if (selectedFiles.length === allFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(allFiles.map((row) => row.documentId));
    }
  };

  const handleOpenFilterDrawer = () => {
    setIsFilterDrawerOpen(true);
  };

  const toggleFilterDrawer = () => {
    setIsFilterDrawerOpen((prev) => !prev);
  };

  const closeFilterDrawer = (e) => {
    setIsFilterDrawerOpen(false);
  };

  const hideFilterDrawer = (e) => {
    const drawer = document.querySelector(".filter-drawer");
    const filterIcon = document.querySelector(".filter-icon");
    const filterIconText = document.querySelector(".filter-icon-text");
    const filterIconIc = document.querySelector(".filter-icon-ic");

    if (
      drawer &&
      !drawer.contains(e.target) &&
      filterIcon &&
      !filterIcon.contains(e.target) &&
      filterIconText &&
      !filterIconText.contains(e.target) &&
      filterIconIc &&
      !filterIconIc.contains(e.target)
    ) {
      setIsFilterDrawerOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", hideFilterDrawer);
    return () => {
      document.removeEventListener("click", hideFilterDrawer);
    };
  }, []);

  const handleScroll = (e) => {
    if (!hasMore && !loading) {
      console.log("No more records to load.");
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop === clientHeight && hasMore && !loading) {
      setPageNumber((prevPage) => prevPage + 1);
    }
  };

  const showSnackbar = (type, message) => {
    setSnackbarState({ open: true, type, message });
  };

  const handleFilterChange = (selectedFilters) => {
    if (selectedFilters.length === 0) {
      setFilteredFiles(allFiles);
    } else {
      const filtered = allFiles.filter((file) =>
        selectedFilters.includes(file.documentTypeId.toString())
      );
      setFilteredFiles(filtered);
    }
    closeFilterDrawer();
  };

  const PropertyList = useMemo(
    () => (
      <List>
        {properties.map((property) => (
          <Box key={property.propertyId}>
            <ListItem
              button
              onClick={() => {
                const isSameClick =
                  parseInt(property.propertyId) === parseInt(selectedProperty);

                handleToggle(
                  isSameClick ? null : property.propertyId,
                  property.propertyName
                );
              }}
            >
              <img
                src={
                  // eslint-disable-next-line eqeqeq
                  selectedProperty == property.propertyId
                    ? FolderMinus
                    : FolderPlus
                }
                alt="folder"
                style={{ width: "20px", height: "20px" }}
              />
              <ListItemText
                disableTypography
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      marginLeft: "5px",
                      color:
                        // eslint-disable-next-line eqeqeq
                        selectedProperty == property.propertyId // eslint-disable-next-line eqeqeq
                          ? "#345dae"
                          : "#212529",
                    }}
                  >
                    {property.propertyName || property.address}
                  </Typography>
                }
              />
            </ListItem>
            <Collapse
              // eslint-disable-next-line eqeqeq
              in={selectedProperty == property.propertyId}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                {property?.projectHeaderList?.projectHeaders.map((item) => (
                  <React.Fragment key={item.projectId}>
                    <ListItem
                      sx={{ pl: 4, cursor: "pointer" }}
                      onClick={() =>
                        handleToggleSelected(item.projectId, item.projectName)
                      }
                    >
                      <Engineering
                        sx={{
                          fontSize: "21px",
                          color:
                            selectedProject === item.projectId
                              ? palette.bluePrimary
                              : "inherit",
                        }}
                      />
                      <ListItemText
                        disableTypography
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "14px",
                              marginLeft: "5px",
                              color:
                                selectedProject === item.projectId
                                  ? "#345dae"
                                  : "#212529",
                            }}
                          >
                            {item.projectName}
                          </Typography>
                        }
                      />
                    </ListItem>

                    {isJobsLoading ? (
                      <ListItem
                        key={"job_loading"}
                        sx={{ pl: 2, cursor: "wait" }}
                      >
                        Loading Jobs...
                      </ListItem>
                    ) : null}

                    <Collapse
                      in={selectedProject === item.projectId}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding sx={{ pl: 4 }}>
                        {jobs?.map((job) => (
                          <ListItem
                            key={job.jobId}
                            sx={{ pl: 2, cursor: "pointer" }}
                            onClick={() =>
                              handleSelectedJob(job.jobId, job.jobName)
                            }
                          >
                            <AssignmentIcon
                              sx={{
                                fontSize: "21px",
                                color:
                                  selectedJob === job.jobId
                                    ? palette.bluePrimary
                                    : "inherit",
                              }}
                            />
                            <ListItemText
                              disableTypography
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "14px",
                                    marginLeft: "5px",
                                    color:
                                      selectedJob === job.jobId
                                        ? "#345dae"
                                        : "#212529",
                                  }}
                                >
                                  {job.jobName}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </Box>
        ))}
        {loading && (
          <Typography sx={{ textAlign: "center" }}>Loading...</Typography>
        )}
        {!hasMore && !loading && (
          <Typography sx={{ textAlign: "center" }}>
            No more records to load.
          </Typography>
        )}
      </List>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      properties,
      selectedProperty,
      selectedProject,
      selectedJob,
      loading,
      hasMore,
      handleToggleSelected,
      isJobsLoading,
      jobs,
    ]
  );

  const handleUploadDialogClose = () => {
    setOpenUploadDialog(false);
  };

  // Prevent body-level horizontal scroll while this page is active
  useEffect(() => {
    const prevOverflowX = document.body.style.overflowX;
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = prevOverflowX;
    };
  }, []);

  if (isLessThanTablet) {
    return (
      <Box
        sx={{
          p: 3,
          m: 2,
          border: "1px solid",
          borderColor: "warning.main",
          borderRadius: 2,
          backgroundColor: "warning.light",
          textAlign: "center",
          maxWidth: 400,
          mx: "auto",
        }}
      >
        <Typography variant="h6" color="warning.dark" gutterBottom>
          Notice:
        </Typography>
        <Typography variant="body1" color="warning.dark">
          For the best experience, please use this page on a device with a
          screen larger than a tablet.
        </Typography>
      </Box>
    );
  }

  return (
    <div
      style={{
        backgroundColor: palette.white,
        overflowX: "hidden",
        width: "100%",
        maxWidth: "100vw",
      }}
    >
      <ModelDialog
        fullWidth
        open={openUploadDialog}
        handleClose={handleUploadDialogClose}
        dialogTitle={
          <FileManagerUploadHeader
            title={selectedProperty === "Company" ? "Company" : listTitle}
            handleClose={handleUploadDialogClose}
          />
        }
        dialogContent={
          <FileManagerUploadContent
            filesBelongsTo={filesBelongsTo}
            selectedData={{
              property: selectedProperty,
              project: selectedProject,
              job: selectedJob,
            }}
            onUploadSuccess={() => {
              handleUploadDialogClose();
              fetchEntityFiles();
            }}
          />
        }
      />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={() => setSnackbarState({ ...snackbarState, open: false })}
      >
        <Alert
          onClose={() => setSnackbarState({ ...snackbarState, open: false })}
          severity={snackbarState.type}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        fullWidth={true}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Warning</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure to delete selected file
            {selectedFiles.length > 1 ? "s" : ""}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmModal(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={() => setMakeDelete(true)} variant="danger">
            Sure
          </Button>
        </DialogActions>
      </Dialog>
      <div
        className="d-flex align-items-center"
        style={{
          backgroundColor: palette.white,
          borderBottom: "1px solid #CCC",
        }}
      >
        <ArrowBack
          style={{ height: "20px", width: "23px", color: "#345DAE" }}
          className="pointer mt-2"
          onClick={() => window.history.back()}
        />
        <Typography
          className="h3 mt-3"
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "15rem",
          }}
        >
          File Manager
        </Typography>
      </div>
      <Box
        sx={{
          display: "flex",
          minWidth: 0,
          width: "100%",
          overflowX: "hidden",
        }}
      >
        <Box
          sx={{
            p: 2,
            height: "100vh",
            overflowY: "auto",
            flex: "0 0 20%",
            boxSizing: "border-box",
            minWidth: 0,
          }}
          onScroll={handleScroll}
          ref={listRef}
        >
          {/* <Typography variant="body" sx={{ borderBottom: "1ps solid #CCC" }}>
            <strong>Property</strong>
          </Typography>
          <hr /> */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #CCC",
              pb: 1,
              mb: 1,
              cursor: "pointer",
              px: 1,
              borderRadius: 1,
              backgroundColor:
                selectedProperty === "company" && filesBelongsTo === "Company"
                  ? "rgba(52, 93, 174, 0.08)"
                  : "transparent",
              borderLeft:
                selectedProperty === "company" && filesBelongsTo === "Company"
                  ? "3px solid #345DAE"
                  : "3px solid transparent",
              "&:hover": {
                backgroundColor: "rgba(52, 93, 174, 0.06)",
              },
            }}
            onClick={() => {
              handleToggle("company", `Company Documents`, "Company");
              // dispatch(fetchCompanyDocumentsAction());
            }}
            role="button"
            aria-current={
              selectedProperty === "company" && filesBelongsTo === "Company"
                ? "true"
                : undefined
            }
          >
            <CorporateFare
              sx={{
                fontSize: 20,
                color:
                  selectedProperty === "company" && filesBelongsTo === "Company"
                    ? "#345DAE"
                    : "#6b7280",
                mr: 1,
              }}
            />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color:
                  selectedProperty === "company" && filesBelongsTo === "Company"
                    ? "#345DAE"
                    : "inherit",
              }}
            >
              View All Company Documents
            </Typography>
          </Box>
          {PropertyList}
        </Box>

        {isFetching ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              backgroundColor: palette.white,
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "80vh",
            }}
          >
            <Typography sx={{ textAlign: "center", justifyContent: "center" }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              backgroundColor: palette.white,
              overflowX: "hidden",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              "& *": { minWidth: 0 },
              "& img": { maxWidth: "100%", height: "auto" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minWidth: 0,
                ml: 1.5,
                mt: 1,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {listTitle}
              </Typography>
              <div className="d-flex justify-content-end">
                <div>
                  <button
                    className="primaryButton"
                    onClick={() => {
                      setOpenUploadDialog(true);
                    }}
                  >
                    <UploadFile sx={{ fontSize: "21px", mr: 1 }} />
                    Upload Files
                  </button>
                </div>
                <div>
                  <IconButton
                    onClick={() => toggleView("list")}
                    sx={{
                      color: viewMode === "list" ? "#FFF" : "#345DAE",
                      backgroundColor:
                        viewMode === "list" ? "#345DAE" : "#f5f5f5",
                      borderRadius: 0,
                    }}
                  >
                    <FormatListBulletedSharp sx={{ fontSize: "21px" }} />
                  </IconButton>
                  <IconButton
                    onClick={() => toggleView("grid")}
                    sx={{
                      color: viewMode === "grid" ? "#FFF" : "#345DAE",
                      backgroundColor:
                        viewMode === "grid" ? "#345DAE" : "#f5f5f5",
                      borderRadius: 0,
                    }}
                  >
                    <GridViewSharp sx={{ fontSize: "21px" }} />
                  </IconButton>
                </div>
                <HiddenOn isHidden={true} fallback={<div className={"me-4"} />}>
                  <div
                    className="pointer d-flex me-3 filter-icon"
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#345DAE",
                      marginTop: "auto",
                      marginBottom: "auto",
                      width: "auto",
                      justifyContent: "end",
                    }}
                    onClick={toggleFilterDrawer}
                  >
                    <Typography
                      variant="body1"
                      className="h6 me-2 filter-icon-text"
                      style={{
                        fontSize: "16px",
                        fontFamily: "Barlow",
                        fontWeight: "700",
                      }}
                    >
                      Filters
                    </Typography>
                    <FilterList
                      style={{ fontSize: "1.5rem" }}
                      className={"filter-icon-ic"}
                    />
                  </div>
                </HiddenOn>
              </div>
            </Box>

            {allFiles.length > 0 && (
              <div
                data-testid={"properties-select-div"}
                className="d-flex"
                style={{ alignItems: "center" }}
              >
                <FormGroup className="ms-3">
                  <FormControlLabel
                    disabled={true}
                    control={<Checkbox sx={{ fontSize: "18px" }} />}
                    label={
                      selectedFiles.length === allFiles.length
                        ? "Deselect All"
                        : "Select All"
                    }
                    onChange={handleSelectAll}
                    checked={selectedFiles.length === allFiles.length}
                  />
                </FormGroup>
                <IconButton
                  onClick={promptDelete}
                  className="ms-1"
                  disabled={selectedFiles.length === 0}
                >
                  <Delete style={{ fontSize: "20px" }} />
                  <Typography
                    variant="body1"
                    sx={{ color: palette.blackPrimary }}
                  >
                    Delete
                  </Typography>
                </IconButton>
              </div>
            )}
            {viewMode === "list" ? (
              <FileManagerList
                title={listTitle}
                showToolbar
                files={filteredFiles}
                fileLabels={{}}
                uploadStatus={{}}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
              />
            ) : (
              <FileGrid files={filteredFiles} />
            )}
          </Box>
        )}
      </Box>
      <Drawer
        open={isFilterDrawerOpen}
        anchor="right"
        variant="temporary"
        onClose={closeFilterDrawer}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 320,
            // Optional: align below the page header; adjust/remove if not desired
            mt: "14%",
            height: "86vh",
          },
        }}
        className="filter-drawer"
      >
        <Filter
          options={documentTypes}
          onFilterChange={handleFilterChange}
          onClose={closeFilterDrawer}
        />
      </Drawer>
    </div>
  );
};

export default FileManager;
